import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Info,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { validatePassword, getStrengthColor, type PasswordValidationResult } from '../../utils/passwordValidation';

interface PasswordStrengthMeterProps {
  password: string;
  showDetails?: boolean;
  showSuggestions?: boolean;
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  showDetails = true,
  showSuggestions = false,
}) => {
  const [showRequirements, setShowRequirements] = React.useState(false);
  
  const validation: PasswordValidationResult = validatePassword(password);
  const strengthColor = getStrengthColor(validation.strength);

  const requirements = [
    { text: 'At least 8 characters', met: password.length >= 8 },
    { text: 'Uppercase letter (A-Z)', met: /[A-Z]/.test(password) },
    { text: 'Lowercase letter (a-z)', met: /[a-z]/.test(password) },
    { text: 'Number (0-9)', met: /\d/.test(password) },
    { text: 'Special character (@$!%*?&)', met: /[@$!%*?&]/.test(password) },
  ];

  if (!password) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      {/* Strength Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
          Password strength:
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <LinearProgress
            variant="determinate"
            value={validation.score}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: strengthColor,
                transition: 'background-color 0.3s ease',
              },
            }}
          />
        </Box>
        <Chip
          label={validation.strength}
          size="small"
          sx={{
            backgroundColor: strengthColor,
            color: validation.strength === 'Fair' ? 'black' : 'white',
            fontWeight: 'bold',
          }}
        />
      </Box>

      {/* Score */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Score: {validation.score}/100
      </Typography>

      {/* Toggle Requirements */}
      {showDetails && (
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="body2"
            onClick={() => setShowRequirements(!showRequirements)}
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {showRequirements ? <VisibilityOff /> : <Visibility />}
            {showRequirements ? 'Hide' : 'Show'} Requirements
          </Typography>
        </Box>
      )}

      {/* Requirements List */}
      <Collapse in={showRequirements}>
        <List dense sx={{ py: 0 }}>
          {requirements.map((req, index) => (
            <ListItem key={index} sx={{ py: 0, px: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {req.met ? (
                  <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                ) : (
                  <Cancel sx={{ color: 'error.main', fontSize: 16 }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={req.text}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: {
                    color: req.met ? 'success.main' : 'text.secondary',
                    textDecoration: req.met ? 'line-through' : 'none',
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>

      {/* Errors */}
      {validation.errors.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {validation.errors.map((error, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Cancel sx={{ color: 'error.main', fontSize: 16 }} />
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Warnings */}
      {validation.warnings.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {validation.warnings.map((warning, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Warning sx={{ color: 'warning.main', fontSize: 16 }} />
              <Typography variant="caption" color="warning.main">
                {warning}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Suggestions */}
      {showSuggestions && !validation.isValid && (
        <Box sx={{ mt: 1, p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Info sx={{ color: 'info.main', fontSize: 16 }} />
            <Typography variant="body2" fontWeight="medium">
              Tips for a stronger password:
            </Typography>
          </Box>
          <Typography variant="caption" component="div">
            • Use a mix of uppercase and lowercase letters
            <br />
            • Include numbers and special characters
            <br />
            • Make it at least 12 characters long
            <br />
            • Avoid personal information or common words
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PasswordStrengthMeter;